/* Mystery Box minicart widget.
 * Shows a configurable "mystery" product offer when a cart item — or a component
 * inside a native-bundle pack line — matches a mystery product's type + Fit option
 * (fit_* tag on the matching product).
 * Business rules: max 1 mystery per order, requires a companion item, the mystery product
 * must stock the companion's exact size (otherwise nothing is offered), only one widget
 * shown on multi-match (first cart item whose size is available wins).
 */
class MysteryBoxMinicart extends HTMLElement {
  constructor() {
    super();
    this.config = window.theme?.mystery_box || null;
    this.mysteryProducts = this.readMysteryProducts();
    this.isOpen = this.config ? this.config.defaultOpen !== false : true;
    this.addInFlight = false;
    this.correctionAttempts = new Map();
    this.hasReceivedCartData = false;
    this.shownProductHandle = null;
    this.shownTriggerIdentifier = null;
    this.shownOfferedSku = null;
    this.lastReportedViewHandle = null;
  }

  /* Re-insertion in side-cart.js's reRenderItemsContainer removes and re-attaches this element,
   * re-firing connectedCallback on every cart re-render. The explicit refresh(cartData) call that
   * follows re-insertion already has fresh data, so deferring this fetch one tick and skipping it
   * once that data has arrived (or is about to) avoids a duplicate /cart?sections=cart-json request
   * racing the explicit refresh. The true first-mount case (Liquid initial render, nobody calls
   * refresh) still fetches, since hasReceivedCartData stays false through the deferred check. */
  connectedCallback() {
    if (!this.config || !this.config.enabled) return;
    setTimeout(() => {
      if (!this.hasReceivedCartData && !this.initialCartLooksEmpty()) this.fetchCartAndRefresh();
    }, 0);
  }

  /* Skips the safety-net fetch storewide when the cart is empty, using the Liquid-rendered
   * `{{ cart.items | json }}` snapshot from side-cart.liquid instead of a network request.
   * A missing or unparseable snapshot is treated as empty, since there is nothing to match. */
  initialCartLooksEmpty() {
    try {
      const node = document.getElementById('mb-club-minicart-cart-items');
      if (!node) return true;
      const items = JSON.parse(node.textContent);
      return !Array.isArray(items) || items.length === 0;
    } catch (error) {
      return true;
    }
  }

  /* The Liquid-rendered `{{ cart.items | json }}` script tag used elsewhere in the minicart
   * has no `tags` field, so matching against it would always fail. Fetch the cart-json section
   * once on connect instead, the same way side-cart.js's getCart() does, since that section
   * output includes `tags` per item (Task 2). */
  async fetchCartAndRefresh() {
    try {
      const response = await fetch('/cart?sections=cart-json');
      const sectionsData = JSON.parse(await response.text());
      const cartJsonSectionHTML = sectionsData['cart-json'];
      const parsedDocument = new DOMParser().parseFromString(cartJsonSectionHTML, 'text/html');
      const cartData = JSON.parse(parsedDocument.querySelector('#shopify-section-cart-json').textContent);
      await this.refresh(cartData);
    } catch (error) {
      console.warn('[Mystery Box] Failed to fetch initial cart data:', error);
    }
  }

  readMysteryProducts() {
    try {
      const node = document.getElementById('mystery-box-data');
      return node ? JSON.parse(node.textContent) : [];
    } catch (error) {
      console.warn('[Mystery Box] Failed to parse mystery-box-data:', error);
      return [];
    }
  }

  countryAllowed() {
    const csv = (this.config.exclusiveCountries || '').trim();
    if (!csv) return true;
    const allowed = csv.split(',').map((code) => code.trim().toUpperCase()).filter(Boolean);
    const current = (window.theme?.current_country || window.Shopify?.country || '').toUpperCase();
    return allowed.includes(current);
  }

  isMysteryItem(item) {
    return this.config.handles.includes(item.handle);
  }

  isEligibleCompanion(item) {
    if (item.gift_card) return false;
    if (this.isMysteryItem(item)) return false;
    const props = Array.isArray(item.properties) ? item.properties : Object.entries(item.properties || {});
    const blockedProps = ['_gift_item', '_shipping_protection', '_hide_on_cart', 'hideOnCart', 'teepack'];
    if (props.some(([key, value]) => blockedProps.includes(key) && value)) return false;
    if (String(item.product_id) === String(window.theme?.mb_club_subscription_product_id)) return false;
    if (Array.isArray(item.line_level_discount_allocations) &&
      item.line_level_discount_allocations.some((allocation) => (allocation.discount_application_title || '').includes('Gift With Purchase'))) return false;
    return true;
  }

  meetsMinimumSubtotal(cartData) {
    const minimumSubtotalCents = (Number(this.config.minSubtotal) || 0) * 100;
    if (minimumSubtotalCents <= 0) return true;
    return (cartData?.total_price || 0) >= minimumSubtotalCents;
  }

  /* A companion whose size the mystery product does not have in stock is skipped rather than
   * offered a different size, so the widget never shows a size the shopper did not pick. */
  findMatch(items) {
    for (const item of items) {
      if (!this.isEligibleCompanion(item)) continue;
      for (const source of this.matchSourcesForItem(item)) {
        const tags = (source.tags || []).map((tag) => String(tag).toLowerCase());
        const companionSize = this.sizeFromOptions(source.options_with_values);
        const match = this.mysteryProducts.find((mystery) =>
          mystery.type === source.product_type &&
          tags.includes(('fit_' + mystery.fit).toLowerCase()) &&
          this.hasAvailableVariantForSize(mystery, companionSize)
        );
        if (match) {
          return {
            mystery: match,
            companionSize,
            triggerIdentifier: this.skuOrVariantId(source.sku, source.variant_id)
          };
        }
      }
    }
    return null;
  }

  /* Tracking events want the companion's SKU when one is set, falling back to its variant id
   * (pack components can carry either, see cart-json.liquid's item_components_meta). */
  skuOrVariantId(sku, variantId) {
    if (sku) return sku;
    return variantId != null ? String(variantId) : null;
  }

  hasAvailableVariantForSize(mystery, size) {
    if (!size) return false;
    const normalizedSize = size.toLowerCase();
    return mystery.variants.some((variant) => variant.available && variant.size.toLowerCase() === normalizedSize);
  }

  /* A pack line's own product is the bundle parent (type "Packs", no fit_* tags); the jeans
   * that can trigger a match live in item_components_meta, serialized by cart-json.liquid
   * because the native item_components JSON carries no product tags. */
  matchSourcesForItem(item) {
    return [
      {
        product_type: item.product_type,
        tags: item.tags,
        options_with_values: item.options_with_values,
        sku: item.sku,
        variant_id: item.variant_id
      },
      ...(item.item_components_meta || [])
    ];
  }

  /* Size of the cart item (or pack component) that triggered the match ("29x30" for jeans,
   * "M" for tees), derived from the same option positions the theme's item templates rely on
   * (option2 = waist or size, option3 = inseam or blank). */
  sizeFromOptions(optionsWithValues) {
    const optionValues = (optionsWithValues || []).map((option) => option.value);
    if (optionValues[1] && optionValues[2]) return optionValues[1] + 'x' + optionValues[2];
    return optionValues[1] || null;
  }

  /* Main entry point, called by side-cart.js after every cart re-render. */
  async refresh(cartData) {
    this.hasReceivedCartData = true;
    if (!this.config || !this.config.enabled || !this.mysteryProducts.length) return this.hide();
    const items = cartData?.items || [];

    await this.enforceBusinessRules(items);

    if (!this.countryAllowed()) return this.hide();
    if (!this.meetsMinimumSubtotal(cartData)) return this.hide();
    const mysteryInCart = items.some((item) => this.isMysteryItem(item));
    if (mysteryInCart) return this.hide();

    const matchResult = this.findMatch(items);
    if (!matchResult) return this.hide();
    const match = matchResult.mystery;
    const availableVariants = match.variants.filter((variant) => variant.available);

    this.render(match, availableVariants, matchResult.companionSize);
    this.classList.remove('hidden');
    this.shownProductHandle = match.handle;
    this.shownTriggerIdentifier = matchResult.triggerIdentifier;
    const offeredVariant = availableVariants.find(
      (variant) => variant.size.toLowerCase() === matchResult.companionSize.toLowerCase()
    );
    this.shownOfferedSku = this.skuOrVariantId(offeredVariant?.sku, offeredVariant?.id);
    this.reportView();
  }

  /* Max qty 1 per mystery line; auto-remove the mystery when it has no companion. */
  async enforceBusinessRules(items) {
    const mysteryLines = items.filter((item) => this.isMysteryItem(item));
    if (!mysteryLines.length) return;

    const hasCompanion = items.some((item) => this.isEligibleCompanion(item));
    for (const line of mysteryLines) {
      const targetQty = hasCompanion ? 1 : 0;
      if (line.quantity !== targetQty) {
        await this.correctLineQuantity(line.key, targetQty);
        return;
      }
    }
    // More than one distinct mystery product: keep the first line only.
    if (mysteryLines.length > 1) {
      await this.correctLineQuantity(mysteryLines[1].key, 0);
    }
  }

  /* Re-renders only when the correction actually succeeds, and gives up after 2 attempts
   * for the same line+target so a failing /cart/change.js call can't loop refresh forever. */
  async correctLineQuantity(lineKey, targetQty) {
    const attemptKey = lineKey + ':' + targetQty;
    const attempts = this.correctionAttempts.get(attemptKey) || 0;
    if (attempts >= 2) return;
    this.correctionAttempts.set(attemptKey, attempts + 1);

    const succeeded = await this.changeLineQuantity(lineKey, targetQty);
    if (succeeded) {
      this.correctionAttempts.delete(attemptKey);
      this.requestCartReRender();
    }
  }

  async changeLineQuantity(lineKey, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: quantity })
      });
      return response.ok;
    } catch (error) {
      console.warn('[Mystery Box] cart change failed:', error);
      return false;
    }
  }

  requestCartReRender() {
    const sideCart = document.querySelector('side-cart');
    if (sideCart && typeof sideCart.getCart === 'function') {
      sideCart.getCart();
    }
  }

  hide() {
    this.classList.add('hidden');
    this.shownProductHandle = null;
    this.shownTriggerIdentifier = null;
    this.shownOfferedSku = null;
    this.lastReportedViewHandle = null;
  }

  /* GTM's "mystery_widget_view" Custom Event trigger fires the mParticle Special Event tag, which
   * reads the offered product from `product_handle`, the companion's SKU/variant id from
   * `trigger_sku` (mapped in GTM as the "mystery_trigger" custom attribute), and the offered
   * mystery variant's SKU from `offered_sku` (mapped as "mystery_offered_sku") — same
   * variable_N_name/value convention already used by the MiniCartDisplay Membership tag.
   * refresh() also runs on page load and on background cart re-renders, so the impression is
   * reported only while the minicart is open, and only once per offered product. */
  reportView() {
    if (!this.shownProductHandle) return;
    if (!this.minicartIsOpen()) return;
    if (this.lastReportedViewHandle === this.shownProductHandle) return;
    this.lastReportedViewHandle = this.shownProductHandle;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'mystery_widget_view',
        product_handle: this.shownProductHandle,
        trigger_sku: this.shownTriggerIdentifier,
        offered_sku: this.shownOfferedSku
      });
    } catch (error) {
      console.warn('[Mystery Box] Failed to push mystery_widget_view:', error);
    }
  }

  minicartIsOpen() {
    return !!document.querySelector('side-cart.cart--open');
  }

  /* Called by side-cart.js when the minicart closes, so reopening it reports a new impression
   * instead of being swallowed by the same-product dedupe. */
  resetViewReporting() {
    this.lastReportedViewHandle = null;
  }

  /* Variant sizes like "31x32" mean waist x inseam (two dropdowns); anything else is a single size dropdown. */
  variantsAreWaistInseam(variants) {
    return variants.every((variant) => /^\d+x\d+$/i.test(variant.size));
  }

  render(match, availableVariants, companionSize) {
    const texts = this.config.texts;
    const accent = this.config.accentColor;
    const isJeans = this.variantsAreWaistInseam(availableVariants);
    const priceHTML = `
      <span class="mystery-box__price" style="color: ${accent}">${this.formatMoney(match.price)}</span>
      ${match.compare_at_price && match.compare_at_price > match.price
        ? `<s class="mystery-box__compare-price">${this.formatMoney(match.compare_at_price)}</s>`
        : ''}
    `;

    this.innerHTML = `
      <div class="mystery-box${this.isOpen ? ' mystery-box--open' : ''}">
        <button type="button" class="mystery-box__header" aria-expanded="${this.isOpen}">
          <span class="mystery-box__badge" style="background-color: ${accent}">${texts.specialOffer}</span>
          <span class="mystery-box__headline">${texts.dealWorthAdding}</span>
          <span class="mystery-box__chevron"></span>
        </button>
        <div class="mystery-box__body">
          <div class="mystery-box__image">
            <img src="${match.featured_image}" alt="${match.title}" loading="lazy">
          </div>
          <div class="mystery-box__details">
            <p class="mystery-box__final-sale" style="color: ${accent}">${texts.finalSale}</p>
            <div class="mystery-box__title-row">
              <div class="mystery-box__title-group">
                <p class="mystery-box__title">${match.title}</p>
                ${match.fit ? `<p class="mystery-box__fit">${match.fit} ${texts.fitSuffix}</p>` : ''}
              </div>
              <div class="mystery-box__prices">${priceHTML}</div>
            </div>
            <div class="mystery-box__controls">
              ${this.renderDropdowns(availableVariants, isJeans, texts, companionSize)}
              <button type="button" class="mystery-box__atc" style="background-color: ${accent}">${texts.add}</button>
            </div>
          </div>
        </div>
      </div>
    `;
    this.bindEvents(match, availableVariants, isJeans);
  }

  /* findMatch guarantees companionSize exists among the available variants, so it is always
   * the preselected option (no fallback to another size). */
  renderDropdowns(variants, isJeans, texts, companionSize) {
    const selectedSize = companionSize.toLowerCase();
    if (!isJeans) {
      const options = variants
        .map((variant) => `<option value="${variant.id}"${variant.size.toLowerCase() === selectedSize ? ' selected' : ''}>${variant.size}</option>`)
        .join('');
      return `
        <div class="mystery-box__dropdown-group">
          <label class="mystery-box__label" for="mystery-box-size">${texts.sizeLabel}</label>
          <select class="mystery-box__dropdown" id="mystery-box-size" data-role="size">${options}</select>
        </div>
      `;
    }
    const [selectedWaist, selectedInseam] = selectedSize.split('x');
    const waists = [...new Set(variants.map((variant) => variant.size.split('x')[0]))];
    const inseamsForSelectedWaist = this.inseamsAvailableForWaist(variants, selectedWaist);
    const waistOptions = waists.map((waist) => `<option value="${waist}"${waist === selectedWaist ? ' selected' : ''}>${waist}</option>`).join('');
    const inseamOptions = inseamsForSelectedWaist.map((inseam) => `<option value="${inseam}"${inseam === selectedInseam ? ' selected' : ''}>${inseam}</option>`).join('');
    return `
      <div class="mystery-box__dropdown-group">
        <label class="mystery-box__label" for="mystery-box-waist">${texts.waistLabel}</label>
        <select class="mystery-box__dropdown" id="mystery-box-waist" data-role="waist">${waistOptions}</select>
      </div>
      <div class="mystery-box__dropdown-group">
        <label class="mystery-box__label" for="mystery-box-inseam">${texts.inseamLabel}</label>
        <select class="mystery-box__dropdown" id="mystery-box-inseam" data-role="inseam">${inseamOptions}</select>
      </div>
    `;
  }

  inseamsAvailableForWaist(variants, waist) {
    return [...new Set(
      variants.filter((variant) => variant.size.split('x')[0] === waist).map((variant) => variant.size.split('x')[1])
    )];
  }

  /* Rebuilds the inseam options to only those that exist for the selected waist, so every
   * selectable waist+inseam combination resolves to a real variant. Keeps the current inseam
   * selected when it is still valid for the new waist. */
  updateInseamOptionsForSelectedWaist(availableVariants) {
    const waistSelect = this.querySelector('[data-role="waist"]');
    const inseamSelect = this.querySelector('[data-role="inseam"]');
    const previousInseam = inseamSelect.value;
    const inseamsForWaist = this.inseamsAvailableForWaist(availableVariants, waistSelect.value);
    const keepPreviousInseam = inseamsForWaist.includes(previousInseam);
    inseamSelect.innerHTML = inseamsForWaist
      .map((inseam, index) => {
        const isSelected = keepPreviousInseam ? inseam === previousInseam : index === 0;
        return `<option value="${inseam}"${isSelected ? ' selected' : ''}>${inseam}</option>`;
      })
      .join('');
  }

  bindEvents(match, availableVariants, isJeans) {
    this.querySelector('.mystery-box__header').addEventListener('click', () => {
      this.isOpen = !this.isOpen;
      this.querySelector('.mystery-box').classList.toggle('mystery-box--open', this.isOpen);
      this.querySelector('.mystery-box__header').setAttribute('aria-expanded', String(this.isOpen));
    });
    if (isJeans) {
      this.querySelector('[data-role="waist"]').addEventListener('change', () => this.updateInseamOptionsForSelectedWaist(availableVariants));
    }
    this.querySelector('.mystery-box__atc').addEventListener('click', () => this.addToCart(match, availableVariants, isJeans));
  }

  selectedVariantId(availableVariants, isJeans) {
    if (!isJeans) return Number(this.querySelector('[data-role="size"]').value);
    const waist = this.querySelector('[data-role="waist"]').value;
    const inseam = this.querySelector('[data-role="inseam"]').value;
    const variant = availableVariants.find((v) => v.size.toLowerCase() === `${waist}x${inseam}`.toLowerCase());
    return variant ? variant.id : null;
  }

  async addToCart(match, availableVariants, isJeans) {
    if (this.addInFlight) return;
    const variantId = this.selectedVariantId(availableVariants, isJeans);
    if (!variantId) {
      console.warn('[Mystery Box] No variant for the selected size combination');
      return;
    }
    this.addInFlight = true;
    this.querySelector('.mystery-box__atc').setAttribute('disabled', 'disabled');
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: { _final_sale: 'yes' } }] })
      });
      if (!response.ok) throw new Error('Add to cart failed: ' + response.status);
      this.requestCartReRender();
    } catch (error) {
      console.warn('[Mystery Box] Add to cart failed:', error);
    } finally {
      this.addInFlight = false;
      const atc = this.querySelector('.mystery-box__atc');
      if (atc) atc.removeAttribute('disabled');
    }
  }

  formatMoney(cents) {
    const amount = cents / 100;
    const formattedAmount = window.Shopify && typeof window.Shopify.formatMoneyAmmount === 'function'
      ? window.Shopify.formatMoneyAmmount(amount)
      : amount.toFixed(2);
    const symbol = window.theme?.cart_currency_symbol || '$';
    return symbol + formattedAmount;
  }
}

if (!customElements.get('mystery-box-minicart')) {
  customElements.define('mystery-box-minicart', MysteryBoxMinicart);
}
